<template>
  <Container :class="$style.container">
    <ButtonBack
      text="Dashboard"
      :to="$options.homeRoute"
    />
    <Container
      :class="$style.innerContainer"
      data-test-id="ViewAll__container"
    >
      <div :class="$style.headerStyle">
        <HeadingMedium>
          {{ displayName }}
        </HeadingMedium>
      </div>
      <ItemListPagination
        :columns="tableColumns"
        :currentPageNum="currentPageNum"
        :indeterminateSelectAll="indeterminateSelectAll"
        :itemComponent="$options.DeviceRow"
        :loading="isListLoading"
        :rowsPerPage="10"
        :sortBy="sortBy"
        :sortDirection="sortDirection"
        :tableData="tableData"
        :totalCount="totalCount"
        @pageChange="handlePageChange"
        @sortColumn="handleSortColumn"
      >
        <template v-slot:toolbar>
          <HomeSearchBar
            :count="totalCount"
            :disabled="false"
            searchPlaceholder="Search"
            :shouldFocusSearch="shouldFocusSearch"
            :toolBarDisplayName="displayName"
            @triggerSearch="handleEnterSearchTerm"
          />
        </template>
      </ItemListPagination>
    </Container>
  </Container>
</template>
<script>
import {
  ButtonBack,
  Container,
  HeadingMedium,
  ItemListPagination,
  Types,
} from '@jumpcloud/ui-components';
import { ItemTypeConsts, ItemTypeRoutes, StoreModuleMap } from 'client/util/Constants';
import { mapActions, mapState } from 'vuex';
import DeviceRow from 'client/HomePageV2/components/DeviceRow.vue';
import HomeSearchBar from 'client/HomePageV2/components/HomeSearchBar.vue';
import HomeWidgetConsts from 'client/HomePageV2/constants/HomeWidgetConsts';
import SystemApi from 'client/api/v2/SystemApi';
import SystemFilters from 'client/components/ItemTable/CollectionFilterComponents/constants/SystemFilters';
import SystemInsightsApi from 'client/api/v2/SystemInsightsApi';
import ViewAllMixin from 'client/HomePageV2/mixins/ViewAllMixin';
import pluralize from 'pluralize';

const administratorModel = 'AdministratorModel';
const { Column } = Types;
const {
  apiPayloadConst,
  widgetTableColumns,
} = HomeWidgetConsts;
const { maxFetchLimit } = apiPayloadConst;
const userDisplayPreferencesModule = StoreModuleMap[ItemTypeConsts.userDisplayPreferences];

export default {
  name: 'ViewAllDevices',

  components: {
    ButtonBack,
    Container,
    HeadingMedium,
    HomeSearchBar,
    ItemListPagination,
  },

  homeRoute: ItemTypeRoutes[ItemTypeConsts.home],

  mixins: [
    ViewAllMixin,
  ],

  data() {
    return {
      sortBy: 'deviceName',
      upTimeDevice: [],
      widgetName: this.$route.params.widgetName,
    };
  },

  DeviceRow,

  computed: {
    ...mapState(administratorModel, [
      'resetKey',
    ]),

    ...mapState(userDisplayPreferencesModule, {
      displaySettings: state => state.displayPreferencesData,
    }),

    displayName() {
      return `Devices with Uptime Greater than ${pluralize('day', this.noOfConfigurableDays, true)}`;
    },

    noOfConfigurableDays() {
      return this.displaySettings.display.deviceNotifications
        .uptimeMetric.additionalDisplaySettings.numberOfDaysToShow;
    },

    tableColumns() {
      return widgetTableColumns[this.widgetName].map(column => new Column(column));
    },

  },

  watch: {
    resetKey: {
      handler() {
        this.handleCreated();
      },
    },
  },

  created() {
    this.fetchTableData();
  },

  methods: {
    ...mapActions(userDisplayPreferencesModule, [
      'fetchDisplayPreferencesData',
    ]),

    async fetchDeviceInfo(filter) {
      this.isListLoading = true;
      let count = this.totalCount;
      const callUsersApi = async (page = 0) => {
        const filters = await this.getFilters(filter, page);
        return SystemApi.fetch(
          null, null, null, null, null, filters,
        );
      };

      const initialRequest = await callUsersApi();
      let apps = initialRequest.results;

      if (count > maxFetchLimit) {
        const pagedRequests = [];
        for (let i = 1; count > maxFetchLimit; count -= maxFetchLimit, i += 1) {
          pagedRequests.push(callUsersApi(i));
        }

        /* Using Promise.all here is preferred to awaiting in the for loop
         due to a significant performance improvement
        */
        const responses = await Promise.all(pagedRequests);

        /* Initialize with initialRequest.results
          so it is included with the aggregated responses
        */
        apps = responses
          .map(resp => resp.results)
          .reduce((all, current) => all.concat(current), initialRequest.results);
      }
      this.data = apps;
      this.isListLoading = false;

      return this.data;
    },

    async fetchDevices() {
      await this.fetchDisplayPreferencesData();
      const uptimeData = await this.fetchUptimeData();
      const response = await this.fetchDeviceInfo(uptimeData);
      if (response.length > 0) {
        this.data = await this.filterData(response, uptimeData);
      } else {
        this.data = [];
      }

      let start = this.currentPageNum * this.rowsPerPage;
      let end = start + this.rowsPerPage;
      this.tableData = this.data.slice(start, end);
      if (!this.tableData.length) {
        if (this.data.length) {
          start = (this.currentPageNum - 1) * this.rowsPerPage;
          end = start + this.rowsPerPage;
          this.currentPageNum -= 1;
          this.tableData = this.data.slice(start, end);
        } else {
          this.currentPageNum = 0;
        }
      }

      this.selectAll = false;
      this.indeterminateSelectAll = false;
    },

    async fetchUptimeData() {
      this.isListLoading = true;
      const xNoOfDays = this.noOfConfigurableDays * 24 * 60 * 60;
      const callApi = async () => SystemInsightsApi.getAllSystemInsights('uptime',
        {
          bulk: true,
          params: { filter: `total_seconds:gte:${xNoOfDays}` },
        });

      // Initial request will be equal to the response of callAPI
      const initialRequest = await callApi();
      let apps = initialRequest;
      let totalCount = initialRequest.length;

      if (totalCount > maxFetchLimit) {
        const pagedRequests = [];
        for (let i = 1; totalCount > maxFetchLimit; totalCount -= maxFetchLimit, i += 1) {
          pagedRequests.push(callApi(i));
        }

        /* Using Promise.all here is preferred to awaiting in the for loop
           due to a significant performance improvement
        */
        const responses = await Promise.all(pagedRequests);
        [apps] = responses;
      }

      this.data = apps;
      this.upTimeDevice = this.data.map(upimeDevice => ({
        // converting seconds to days
        upTimeDays: Math.floor(upimeDevice.total_seconds / (3600 * 24)),
        id: upimeDevice.system_id,
      }));
      this.totalCount = this.data.length;
      this.isListLoading = false;

      return this.upTimeDevice;
    },
    async filterData(arr1, arr2) {
      const res = [];
      arr1.forEach(el1 => arr2.forEach(el2 => {
        if (el2.id === el1.id) {
          res.push({
            displayName: el1.displayName,
            status: el1.active ? 'Active' : 'Inactive',
            os: el1.os,
            upTime: `${pluralize('day', el2.upTimeDays, true)}`,
            timeStamp: el2.upTimeDays,
            id: el2.id,
            osFamily: el1.osFamily,
          });
        }
      }));

      return res;
    },

    async getFilters(filter, page) {
      /* we can pass only 100 Id's at a time so
      we are slicing the array of ids and joining them to pass as filter
      */
      const ids = await filter.map(user => user.id)
        .slice(page * maxFetchLimit, (page * maxFetchLimit) + maxFetchLimit)
        .join('|');

      const filters = await SystemFilters.getId(ids);

      return filters;
    },

    handlePageChange(paginationData) {
      this.isListLoading = true;
      if (paginationData) {
        const { currentPageNum, rowsPerPage } = paginationData;
        this.currentPageNum = currentPageNum;
        this.rowsPerPage = rowsPerPage;
      }
      const start = this.currentPageNum * this.rowsPerPage;
      const end = start + this.rowsPerPage;
      if (this.search) {
        this.tableData = this.filteredData.slice(start, end);
      } else {
        this.tableData = this.data.slice(start, end);
      }
      this.isListLoading = false;
    },
  },
};
</script>
<style module>
.container {
  background-color: var(--jc-color-disabled);
  display: inherit;
  height: 100%;
  padding: var(--jc-spacer-x-small);
}

.innerContainer {
  background-color: var(--jc-color-surface-light);
  margin-top: var(--jc-spacer);
  width: 100%;
}

.headerStyle {
  display: flex;
  justify-content: space-between;
  padding-bottom: var(--jc-spacer-medium);
  padding-top: var(--jc-spacer-medium);
  width: 90%;
}
</style>
